import type { IHttpClient } from "@/core/interfaces";
import { Registry } from "@/shared/infra/dependency-injection/registry";

import { cabecalhoIdempotencia, readEnvelopeData } from "./financeiro-obras.gateway";
import type { LookupRequestOptions, OpcoesMutacao } from "./financeiro-obras.gateway";
import type {
  AtualizarProjecaoBody,
  CategoriaProjecao,
  CriarProjecaoBody,
  MoedaOpcao,
  OpcaoProjecao,
  ProjecaoAtualizada,
  ProjecaoCriada,
  SimulacaoProjecao,
  SimularProjecaoBody,
} from "./financeiro-obras.projecao.types";

export const FINANCEIRO_PROJECAO_PATHS = {
  MOEDAS: "/api/financeiro/moedas",
  DISTRIBUICOES_COLETOR: "/api/financeiro/distribuicoes-coletor",
  CURVAS_MENSALIZACAO: "/api/financeiro/curvas-mensalizacao",
  SIMULACAO: (obraId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/projecoes-financeiras/simulacao`,
  CRIAR: (obraId: string, obraEscopoMacroId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/escopos-macro/${encodeURIComponent(obraEscopoMacroId)}/projecao-financeira`,
} as const;

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

function lerLista<T>(payload: unknown): T[] {
  const data =
    typeof payload === "object" && payload !== null && "data" in payload
      ? (payload as { data: unknown }).data
      : payload;
  if (!Array.isArray(data)) throw new Error("Resposta inválida do bff.");
  return data as T[];
}

export async function listMoedasProjecao({ signal }: LookupRequestOptions = {}): Promise<
  MoedaOpcao[]
> {
  const response = await getHttpClient().get<unknown>(FINANCEIRO_PROJECAO_PATHS.MOEDAS, { signal });
  return lerLista<MoedaOpcao>(response.data);
}

export async function listDistribuicoesColetor(
  categoria: CategoriaProjecao,
  { signal }: LookupRequestOptions = {},
): Promise<OpcaoProjecao[]> {
  const response = await getHttpClient().get<unknown>(
    FINANCEIRO_PROJECAO_PATHS.DISTRIBUICOES_COLETOR,
    { params: { categoria }, signal },
  );
  return lerLista<OpcaoProjecao>(response.data);
}

export async function listCurvasMensalizacao(
  categoria: CategoriaProjecao,
  { signal }: LookupRequestOptions = {},
): Promise<OpcaoProjecao[]> {
  const response = await getHttpClient().get<unknown>(
    FINANCEIRO_PROJECAO_PATHS.CURVAS_MENSALIZACAO,
    { params: { categoria }, signal },
  );
  return lerLista<OpcaoProjecao>(response.data);
}

export async function simularProjecao(
  obraId: string,
  body: SimularProjecaoBody,
  { signal }: LookupRequestOptions = {},
): Promise<SimulacaoProjecao> {
  const response = await getHttpClient().post<unknown>(
    FINANCEIRO_PROJECAO_PATHS.SIMULACAO(obraId),
    body,
    { signal },
  );
  return readEnvelopeData<SimulacaoProjecao>(response.data);
}

export async function criarProjecaoFinanceira(
  obraId: string,
  obraEscopoMacroId: string,
  body: CriarProjecaoBody,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<ProjecaoCriada> {
  const response = await getHttpClient().post<unknown>(
    FINANCEIRO_PROJECAO_PATHS.CRIAR(obraId, obraEscopoMacroId),
    body,
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );
  return readEnvelopeData<ProjecaoCriada>(response.data);
}

export async function atualizarProjecaoFinanceira(
  obraId: string,
  obraEscopoMacroId: string,
  body: AtualizarProjecaoBody,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<ProjecaoAtualizada> {
  const response = await getHttpClient().put<unknown>(
    FINANCEIRO_PROJECAO_PATHS.CRIAR(obraId, obraEscopoMacroId),
    body,
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );
  return readEnvelopeData<ProjecaoAtualizada>(response.data);
}
